import moment from "moment";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";
import useFigures from "../utils/useFigures";
import useItems from "../utils/useItems";
export default function Summary({ open, close }) {
  const { salesData } = useFigures();
  const { costInStock } = useItems();
  const allItems = useSelector((state) => state.items.Items);
  const expenses = useSelector((state) => state.expenses.Expenses);
  const inStock = allItems?.filter((item) => Number(item.quantity) > 0);

  const salesRecords = useSelector((state) => state.sales.Sales);
  const totalSalesFigure = useMemo(
    () => salesRecords?.reduce((a, b) => a + b.cost, 0),
    [salesRecords]
  );
  const totalProfit = useMemo(
    () =>
      salesRecords
        ?.filter((sale) => sale.profit !== undefined)
        ?.reduce((a, b) => a + b?.profit, 0),
    [salesRecords]
  );
  const totalExpenses = useMemo(
    () => expenses.reduce((a, b) => a + b?.total_cost, 0),
    [expenses]
  );
  const netProfit = totalProfit - totalExpenses;
  const expiredItems = allItems?.filter(
    (item) =>
      item?.expires && moment(item?.expiry, "DD/MM/YYYY").isBefore(moment())
  );
  const expCost = useMemo(
    () => expiredItems?.reduce((a, b) => a + Number(b.quantity) * b.price, 0),
    [expiredItems]
  );

  return (
    <Dialog open={open} onClose={close}>
      <DialogTitle>
        Comprehensive Store Summary as at{" "}
        <b>{moment().format("ddd, DD MMM YYYY")}</b>
      </DialogTitle>
      <DialogContent>
        <table>
          <thead>
            <tr className="store-details-tr">
              <th className="store-details-th">Key</th>
              <th className="store-details-th">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Total No. of items Sold:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {salesRecords?.reduce((a, b) => a + b.quantity, 0)}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Total Sales:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">{CurrencyFormatter(totalSalesFigure)}</b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Average Sales:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(
                    totalSalesFigure / salesRecords.length || 0
                  )}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Total Retail Sales:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(salesData?.retail)}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Average Retail Sales:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(
                    salesData?.retail /
                      salesRecords.filter((sale) => sale.sale_type === "retail")
                        .length || 0
                  )}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Total Wholesale Sales:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(salesData?.wholesale)}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Average Wholesale Sales:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(
                    salesData?.wholesale /
                      salesRecords.filter(
                        (sale) => sale.sale_type === "wholesale"
                      ).length || 0
                  )}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Total Discounted Sales:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(salesData?.agreed)}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Average Discounted Sales:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(
                    salesData?.agreed /
                      salesRecords.filter(
                        (sale) => sale.sale_type === "agreed price"
                      ).length || 0
                  )}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Total Profit:</span>
              </td>
              <td
                className={
                  totalProfit < 1 ? `store-details-td red` : `store-details-td`
                }
              >
                <b className="meaning">{CurrencyFormatter(totalProfit)}</b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Average Profit:</span>
              </td>
              <td
                className={
                  totalProfit < 1 ? `store-details-td red` : `store-details-td`
                }
              >
                <b className="meaning">
                  {CurrencyFormatter(totalProfit / salesRecords.length || 0)}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Retail Profit:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(salesData?.retail_profit)}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Wholesale Profit:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(salesData?.wholesale_profit)}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Discounted Profit:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(salesData?.agreed_profit)}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Total Expenses:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">{CurrencyFormatter(totalExpenses)}</b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Net Profit:</span>
              </td>
              <td
                className={
                  netProfit < 1 ? `store-details-td red` : `store-details-td`
                }
              >
                <b className="meaning">{CurrencyFormatter(netProfit)}</b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                {" "}
                <span className="icon">No. of items in stock:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">{inStock?.length}</b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">No. of pieces of items in stock:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {inStock?.reduce((a, b) => a + Number(b.quantity), 0)}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">
                  Total Cost of Items in stock(+expired):
                </span>
              </td>
              <td className="store-details-td">
                <b className="meaning">{CurrencyFormatter(costInStock)}</b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">No. of expired items:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">{expiredItems?.length}</b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">No. of pieces of expired items:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {expiredItems?.reduce((a, b) => a + Number(b.quantity), 0)}
                </b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Total Cost of Expired Items:</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">{CurrencyFormatter(expCost)}</b>
              </td>
            </tr>
            <tr className="store-details-tr">
              <td className="store-details-td">
                <span className="icon">Floating cash(-expired):</span>
              </td>
              <td className="store-details-td">
                <b className="meaning">
                  {CurrencyFormatter(costInStock - expCost)}
                </b>
              </td>
            </tr>
          </tbody>
        </table>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>
          <Close className="cancel" />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
